/**
 * The all-regions day strip (BON-534).
 *
 * Seven columns, always — a week with nothing on Monday still shows a Monday,
 * or the shape of the week stops being readable at a glance. The dates arrive
 * as plain `YYYY-MM-DD` calendar days already resolved on the venue's clock, so
 * they are split rather than parsed: `new Date('2026-05-17')` is midnight UTC
 * and would read as the 16th anywhere west of Greenwich.
 */
import type { OverviewStrip } from './overview-types';
import { WeekStripDay } from './WeekStripDay';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** `2026-05-17` → its parts, with no timezone anywhere near it. */
function dayParts(date: string): { year: number; month: number; day: number } {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month, day };
}

const weekdayOf = (date: string): string => {
  const { year, month, day } = dayParts(date);
  return WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
};

/** `2026-05-17` → `5/17`, the form the strip has room for. */
const shortDate = (date: string): string => {
  const { month, day } = dayParts(date);
  return `${month}/${day}`;
};

/** `2026-05-17` → `17 May 2026`, for the group label. */
const spokenDate = (date: string): string => {
  const { year, month, day } = dayParts(date);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

export interface WorkshopWeekStripProps {
  strip: OverviewStrip;
  /** Phone widths: weekday labels drop to a single letter so seven columns
   *  still fit on one row. A scroller would hide the week's shape. */
  compact?: boolean;
}

/**
 * Everything the phone layout shrinks, decided once rather than per cell.
 * The roomy figures are the design's (`admin-overview.jsx`): cells pad
 * `7px 8px 8px` over a 64px floor, the count sits at 20px.
 */
const SIZES = {
  roomy: { pad: '7px 8px 8px', align: 'stretch', weekday: 10, count: 20, date: 9.5, minHeight: 64 },
  tight: { pad: '6px 2px 7px', align: 'center', weekday: 9.5, count: 17, date: 8.5, minHeight: 56 },
} as const;

/**
 * The wide strip is a card; the phone strip is not.
 *
 * The design's phone layout puts the label and the seven cells straight onto
 * the page — the cells already carry their own borders, so a card around them
 * adds a second frame and an inset the design does not have.
 */
const CARD_PADDING = { roomy: '12px 18px 14px', tight: '0' } as const;

export function WorkshopWeekStrip({ strip, compact = false }: WorkshopWeekStripProps) {
  const busiest = Math.max(0, ...strip.days.map((day) => day.workshopCount));
  const size = compact ? SIZES.tight : SIZES.roomy;

  return (
    <section
      className={compact ? undefined : 'nb-card nb-card-tight'}
      style={{
        flex: '0 0 auto',
        padding: compact ? CARD_PADDING.tight : CARD_PADDING.roomy,
        background: compact ? 'transparent' : undefined,
      }}
      role="group"
      aria-label={`Workshops, ${spokenDate(strip.fromDate)} to ${spokenDate(strip.toDate)}`}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <h3 className="nb-display" style={{ margin: 0, fontSize: 14, letterSpacing: '.04em' }}>
          This week · all regions
        </h3>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: 'var(--nb-subtle)', whiteSpace: 'nowrap' }}>
          {strip.total} workshops
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 6 }}>
        {strip.days.map((day) => (
          <WeekStripDay
            key={day.date}
            day={day}
            weekday={weekdayOf(day.date)}
            shortDate={shortDate(day.date)}
            // Nothing is "busiest" in a week with nothing scheduled.
            isBusiest={busiest > 0 && day.workshopCount === busiest}
            compact={compact}
            size={size}
          />
        ))}
      </div>
    </section>
  );
}
